import React, { useMemo, useState } from "react";
import { Box, ThemeProvider } from "@mui/material";
import { Form, Modal, Spinner, Table } from "react-bootstrap";
import { FaLinkedin, FaHackerrank, FaGithub, FaVideo, FaFileAlt } from "react-icons/fa";
import { SiLeetcode } from "react-icons/si";
import { IoIosRemoveCircleOutline } from "react-icons/io";
import { PiSortDescendingLight, PiSortAscendingLight } from "react-icons/pi";
import { HiOutlineDotsVertical } from "react-icons/hi";
import authService from "../utils/authService";
import StudentResumeView from "./StudentResumeView";
import { mapStudentToResumeData, type ResumeStudentSource } from "../utils/mapStudentToResumeData";
import { hirerResumeTheme } from "../utils/hirerResumeTheme";
import { compareMaybeNumeric } from "../utils/numericSortUtils";
import { resolveMediaUrl } from "../utils/mediaUrl";

export interface PlacementTableVideoItem {
  type: string;
  video_id: string;
  status?: string;
  otp?: string;
  playback_info?: string;
}

export interface PlacementTableSecondarySkill {
  skill_id: string;
  skill_name: string;
  level: string;
  description?: string;
  cert_url?: string;
  type?: string;
}

export interface PlacementTableProjectDetail {
  type: string;
  description: string;
}

export interface PlacementTableCertificateDetail {
  name: string;
  cert_url?: string;
}

export interface PlacementTableStudent {
  student_id: string;
  student_name: string;
  college?: string;
  branch?: string;
  cgpa?: number;
  score?: string | number;
  score_percentage?: number;
  total_score?: string;
  category?: string;
  grade?: string;
  lsa?: number | string;
  email?: string;
  phone?: string;
  leetcode?: string | null;
  hackerrank?: string | null;
  linkedin?: string | null;
  github?: string | null;
  profile_pic_url?: string;
  shortlisted?: boolean;
  hobbies?: string;
  resume_objective?: string;
  subject_scores?: Record<string, number | string | null | undefined>;
  primary_skills?: string[];
  secondary_skills?: PlacementTableSecondarySkill[];
  videos?: PlacementTableVideoItem[];
  student_education_details?: Array<{
    degree?: string;
    institute?: string;
    branch?: string;
    cgpa?: number | string;
    yop?: number | string;
  }>;
  student_project_details?: PlacementTableProjectDetail[];
  student_primary_certificate_details?: PlacementTableCertificateDetail[];
  student_secondary_certificate_details?: PlacementTableCertificateDetail[];
}

interface PlacementStudentsTableProps {
  students: PlacementTableStudent[];
  loading: boolean;
  emptyMessage: string;
  selectionMode: "checkbox" | "remove" | "none";
  selectColumnLabel?: string;
  isSelected?: (studentId: string) => boolean;
  onToggle?: (student: PlacementTableStudent) => void;
  onRemove?: (student: PlacementTableStudent) => void;
  onRowClick?: (student: PlacementTableStudent) => void;
  rowClickable?: boolean;
  showProfileColumns?: boolean;
  showSelectAll?: boolean;
  isAllSelected?: boolean;
  onToggleSelectAll?: () => void;
}

const formatCategoryColumnAbbrev = (category: string | null | undefined): string => {
  if (category == null || String(category).trim() === "") return "-";
  const s = String(category).trim();
  if (s.length <= 2) return s;
  return s.slice(0, 2);
};

const PlacementStudentsTable: React.FC<PlacementStudentsTableProps> = ({
  students,
  loading,
  emptyMessage,
  selectionMode,
  selectColumnLabel = "Select",
  isSelected,
  onToggle,
  onRemove,
  onRowClick,
  rowClickable = false,
  showProfileColumns = true,
  showSelectAll = false,
  isAllSelected = false,
  onToggleSelectAll,
}) => {
  const [projectsModalStudent, setProjectsModalStudent] = useState<PlacementTableStudent | null>(null);
  const [certificatesModalStudent, setCertificatesModalStudent] = useState<PlacementTableStudent | null>(null);
  const [projectsModalTab, setProjectsModalTab] = useState<string>("0");
  const [certificatesModalTab, setCertificatesModalTab] = useState<string>("primary-0");
  const [certificatesModalMainTab, setCertificatesModalMainTab] = useState<"primary" | "secondary">("primary");
  const [videoModalStudent, setVideoModalStudent] = useState<PlacementTableStudent | null>(null);
  const [videoModalVideos, setVideoModalVideos] = useState<PlacementTableVideoItem[]>([]);
  const [videoModalTab, setVideoModalTab] = useState<string>("0");
  const [videoModalPlayback, setVideoModalPlayback] = useState<{ otp: string; playback_info: string } | null>(null);
  const [videoModalLoading, setVideoModalLoading] = useState(false);
  const [videoModalError, setVideoModalError] = useState<string | null>(null);
  const [profilePicModal, setProfilePicModal] = useState<{ url: string; name: string } | null>(null);
  const [skillsModalStudent, setSkillsModalStudent] = useState<PlacementTableStudent | null>(null);
  const [skillsModalTab, setSkillsModalTab] = useState<string>("0");
  const [resumePreviewStudent, setResumePreviewStudent] = useState<PlacementTableStudent | null>(null);
  const [sortField, setSortField] = useState<
    "student_name" | "college" | "branch" | "cgpa" | "lsa" | "score" | "category" | "grade" | ""
  >("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const projectsCount = (s: PlacementTableStudent) => s.student_project_details?.length ?? 0;
  const videosCount = (s: PlacementTableStudent) => s.videos?.length ?? 0;
  const skillsCount = (s: PlacementTableStudent) => s.secondary_skills?.length ?? 0;
  const certificatesCount = (s: PlacementTableStudent) =>
    (s.student_primary_certificate_details?.length ?? 0) +
    (s.student_secondary_certificate_details?.length ?? 0);

  const fetchVideoForTab = async (videoId: string) => {
    setVideoModalPlayback(null);
    setVideoModalError(null);
    setVideoModalLoading(true);
    try {
      const res = await authService.get(`student/videos/${videoId}/`);
      const otp = res.data?.otp ?? "";
      const playback = res.data?.playback_info ?? "";
      if (otp && playback) setVideoModalPlayback({ otp, playback_info: playback });
      else setVideoModalError("Video not available");
    } catch {
      setVideoModalError("Failed to load video");
    } finally {
      setVideoModalLoading(false);
    }
  };

  const handleSort = (field: "student_name" | "college" | "branch" | "cgpa" | "lsa" | "score" | "category" | "grade") => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortField(field);
    setSortDirection("asc");
  };

  const SortIcon = ({ field }: { field: string }) =>
    sortField === field ? (
      sortDirection === "asc" ? (
        <PiSortDescendingLight size={16} />
      ) : (
        <PiSortAscendingLight size={16} />
      )
    ) : (
      <HiOutlineDotsVertical size={16} />
    );

  const sortedStudents = useMemo(() => {
    if (!sortField) return students;
    return [...students].sort((a, b) => {
      const valueForField = (student: PlacementTableStudent) => {
        if (sortField === "score") return student.score ?? student.score_percentage ?? "";
        return student[sortField];
      };
      const av = valueForField(a);
      const bv = valueForField(b);
      return compareMaybeNumeric(av, bv, sortDirection);
    });
  }, [students, sortDirection, sortField]);

  const modalBodyScrollStyle: React.CSSProperties = {
    maxHeight: "min(75vh, calc(100dvh - 9rem))",
    minHeight: 0,
  };

  const getProjectTabLabels = (projects: PlacementTableProjectDetail[] = []) => {
    const typeCounts: Record<string, number> = {};
    projects.forEach((p) => {
      const t = p.type || "Project";
      typeCounts[t] = (typeCounts[t] ?? 0) + 1;
    });
    const typeIndex: Record<string, number> = {};
    return projects.map((p, i) => {
      const type = p.type || "Project";
      const total = typeCounts[type] ?? 1;
      const idx = (typeIndex[type] = (typeIndex[type] ?? 0) + 1);
      return { key: String(i), label: total > 1 ? `${type}${idx}` : type };
    });
  };

  const getVideoTabLabels = (videos: PlacementTableVideoItem[] = []) => {
    const typeCounts: Record<string, number> = {};
    videos.forEach((v) => {
      const t = v.type || "Video";
      typeCounts[t] = (typeCounts[t] ?? 0) + 1;
    });
    const typeIndex: Record<string, number> = {};
    return videos.map((v, i) => {
      const type = v.type || "Video";
      const total = typeCounts[type] ?? 1;
      const idx = (typeIndex[type] = (typeIndex[type] ?? 0) + 1);
      return { key: String(i), label: total > 1 ? `${type}${idx}` : type };
    });
  };

  const openVideoStudent = (e: React.MouseEvent, student: PlacementTableStudent) => {
    e.stopPropagation();
    const videos = student.videos ?? [];
    if (videos.length === 0) return;
    setVideoModalStudent(student);
    setVideoModalVideos(videos);
    setVideoModalTab("0");
    setVideoModalError(null);
    const first = videos[0];
    if (first?.otp && first?.playback_info) {
      setVideoModalPlayback({ otp: first.otp, playback_info: first.playback_info });
      setVideoModalLoading(false);
    } else if (first?.video_id) {
      setVideoModalPlayback(null);
      setVideoModalLoading(true);
      authService
        .get(`student/videos/${first.video_id}/`)
        .then((res) => {
          const otp = res.data?.otp ?? "";
          const playback = res.data?.playback_info ?? "";
          if (otp && playback) setVideoModalPlayback({ otp, playback_info: playback });
          else setVideoModalError("Video not available");
        })
        .catch(() => setVideoModalError("Failed to load video"))
        .finally(() => setVideoModalLoading(false));
    } else {
      setVideoModalPlayback(null);
      setVideoModalError("Video not available");
    }
  };

  const renderProjectsModalContent = () => {
    if (!projectsModalStudent) return null;
    const projects = projectsModalStudent.student_project_details ?? [];
    const tabs = getProjectTabLabels(projects);
    if (projects.length === 0) {
      return (
        <div className="flex-grow-1 min-h-0 overflow-auto px-3 py-3">
          <p className="text-muted mb-0">No projects.</p>
        </div>
      );
    }
    return (
      <>
        <div className="flex-shrink-0 px-3 pt-3 pb-2 border-bottom bg-white">
          <div className="d-flex flex-row flex-nowrap gap-1">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`btn btn-sm flex-shrink-0 ${projectsModalTab === t.key ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => setProjectsModalTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-grow-1 min-h-0 overflow-auto px-3 py-3">
          <div className="p-2 border rounded bg-light">
            {projects[Number(projectsModalTab)] && (
              <div>
                <div><strong>Type:</strong> {projects[Number(projectsModalTab)].type || "-"}</div>
                <div className="mt-2">
                  <strong>Description:</strong>
                  {projects[Number(projectsModalTab)].description ? (
                    <div className="mt-1" dangerouslySetInnerHTML={{ __html: projects[Number(projectsModalTab)].description }} />
                  ) : (
                    "-"
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  const renderSkillsModalContent = () => {
    if (!skillsModalStudent) return null;
    const skills = skillsModalStudent.secondary_skills ?? [];
    if (skills.length === 0) {
      return (
        <div className="flex-grow-1 min-h-0 overflow-auto px-3 py-3">
          <p className="text-muted mb-0">No skills.</p>
        </div>
      );
    }
    const singleSkill = skills.length === 1;
    const current = skills[Number(skillsModalTab)];
    if (!current) {
      return (
        <div className="flex-grow-1 min-h-0 overflow-auto px-3 py-3">
          <p className="text-muted mb-0">No skill selected.</p>
        </div>
      );
    }
    return (
      <>
        {!singleSkill && (
          <div className="flex-shrink-0 px-3 pt-3 pb-2 border-bottom bg-white">
            <div className="d-flex flex-row flex-nowrap gap-1">
              {skills.map((s, i) => (
                <button
                  key={s.skill_id}
                  type="button"
                  className={`btn btn-sm flex-shrink-0 ${skillsModalTab === String(i) ? "btn-primary" : "btn-outline-secondary"}`}
                  onClick={() => setSkillsModalTab(String(i))}
                >
                  {s.skill_name || `Skill ${i + 1}`}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="flex-grow-1 min-h-0 overflow-auto px-3 py-3">
          <div className="p-2 border rounded bg-light">
            <div className="mt-2"><strong>Level:</strong> {current.level || "-"}</div>
            {current.description != null && current.description !== "" && (
              <div className="mt-2"><strong>Description:</strong> {current.description}</div>
            )}
            {current.cert_url && (
              <div className="mt-2">
                <strong>Certificate:</strong>
                <div className="mt-1">
                  <Box
                    component="img"
                    src={resolveMediaUrl(current.cert_url)}
                    alt={`${current.skill_name} certificate`}
                    sx={{ maxWidth: "100%", maxHeight: 300, display: "block" }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  const renderCertificatesModalContent = () => {
    if (!certificatesModalStudent) return null;
    const prim = certificatesModalStudent.student_primary_certificate_details ?? [];
    const sec = certificatesModalStudent.student_secondary_certificate_details ?? [];
    return (
      <>
        <div className="flex-shrink-0 px-3 pt-3 pb-2 border-bottom bg-white">
          <div className="d-flex flex-row flex-nowrap align-items-center gap-1">
            <div className="d-flex flex-row flex-nowrap gap-1 flex-shrink-0">
              <button
                type="button"
                className={`btn btn-sm ${certificatesModalMainTab === "primary" ? "btn-primary" : "btn-outline-secondary"}`}
                disabled={prim.length === 0}
                onClick={() => {
                  setCertificatesModalMainTab("primary");
                  setCertificatesModalTab("primary-0");
                }}
              >
                Primary ({prim.length})
              </button>
              <button
                type="button"
                className={`btn btn-sm ${certificatesModalMainTab === "secondary" ? "btn-primary" : "btn-outline-secondary"}`}
                disabled={sec.length === 0}
                onClick={() => {
                  setCertificatesModalMainTab("secondary");
                  setCertificatesModalTab("secondary-0");
                }}
              >
                Secondary ({sec.length})
              </button>
            </div>
            {certificatesModalMainTab === "primary" && prim.length > 1 && (
              <div className="d-flex flex-row flex-nowrap gap-1 ms-auto flex-shrink-0">
                {prim.map((_, i) => (
                  <button
                    key={`primary-${i}`}
                    type="button"
                    className={`btn btn-sm btn-outline-primary ${certificatesModalTab === `primary-${i}` ? "active" : ""}`}
                    onClick={() => setCertificatesModalTab(`primary-${i}`)}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
            {certificatesModalMainTab === "secondary" && sec.length > 1 && (
              <div className="d-flex flex-row flex-nowrap gap-1 ms-auto flex-shrink-0">
                {sec.map((_, i) => (
                  <button
                    key={`secondary-${i}`}
                    type="button"
                    className={`btn btn-sm btn-outline-primary ${certificatesModalTab === `secondary-${i}` ? "active" : ""}`}
                    onClick={() => setCertificatesModalTab(`secondary-${i}`)}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex-grow-1 min-h-0 overflow-auto px-3 py-3">
          {certificatesModalMainTab === "primary" && prim.length > 0 && (
            <>
              <div className="p-2 border rounded bg-light">
                {prim.length > 1
                  ? prim[parseInt(certificatesModalTab.replace("primary-", ""), 10)] && (
                    <div>
                      <div><strong>Name:</strong> {prim[parseInt(certificatesModalTab.replace("primary-", ""), 10)].name}</div>
                      {prim[parseInt(certificatesModalTab.replace("primary-", ""), 10)].cert_url && (
                        <Box
                          component="img"
                          src={resolveMediaUrl(prim[parseInt(certificatesModalTab.replace("primary-", ""), 10)].cert_url)}
                          alt={prim[parseInt(certificatesModalTab.replace("primary-", ""), 10)].name}
                          sx={{ maxWidth: "100%", maxHeight: 300, display: "block" }}
                        />
                      )}
                    </div>
                  )
                  : prim[0] && (
                    <div>
                      <div><strong>Name:</strong> {prim[0].name}</div>
                      {prim[0].cert_url && (
                        <Box component="img" src={resolveMediaUrl(prim[0].cert_url)} alt={prim[0].name} sx={{ maxWidth: "100%", maxHeight: 300, display: "block" }} />
                      )}
                    </div>
                  )}
              </div>
            </>
          )}
          {certificatesModalMainTab === "secondary" && sec.length > 0 && (
            <>
              <div className="p-2 border rounded bg-light">
                {sec.length > 1
                  ? sec[parseInt(certificatesModalTab.replace("secondary-", ""), 10)] && (
                    <div>
                      <div><strong>Name:</strong> {sec[parseInt(certificatesModalTab.replace("secondary-", ""), 10)].name}</div>
                      {sec[parseInt(certificatesModalTab.replace("secondary-", ""), 10)].cert_url && (
                        <Box
                          component="img"
                          src={resolveMediaUrl(sec[parseInt(certificatesModalTab.replace("secondary-", ""), 10)].cert_url)}
                          alt={sec[parseInt(certificatesModalTab.replace("secondary-", ""), 10)].name}
                          sx={{ maxWidth: "100%", maxHeight: 300, display: "block" }}
                        />
                      )}
                    </div>
                  )
                  : sec[0] && (
                    <div>
                      <div><strong>Name:</strong> {sec[0].name}</div>
                      {sec[0].cert_url && (
                        <Box component="img" src={resolveMediaUrl(sec[0].cert_url)} alt={sec[0].name} sx={{ maxWidth: "100%", maxHeight: 300, display: "block" }} />
                      )}
                    </div>
                  )}
              </div>
            </>
          )}
          {certificatesModalMainTab === "primary" && prim.length === 0 && <p className="text-muted mb-0">No primary certificates.</p>}
          {certificatesModalMainTab === "secondary" && sec.length === 0 && <p className="text-muted mb-0">No secondary certificates.</p>}
        </div>
      </>
    );
  };

  const renderVideoModalContent = () => {
    if (!videoModalStudent || videoModalVideos.length === 0) {
      return (
        <div className="flex-grow-1 min-h-0 overflow-auto px-3 py-3">
          <p className="text-muted mb-0">No videos.</p>
        </div>
      );
    }
    const tabs = getVideoTabLabels(videoModalVideos);
    const currentVideo = videoModalVideos[Number(videoModalTab)];
    const videoUrl = videoModalPlayback
      ? `https://player.vdocipher.com/v2/?otp=${videoModalPlayback.otp}&playbackInfo=${videoModalPlayback.playback_info}`
      : null;
    const singleVideo = videoModalVideos.length === 1;
    return (
      <>
        {!singleVideo && (
          <div className="flex-shrink-0 px-3 pt-3 pb-2 border-bottom bg-white">
            <div className="d-flex flex-row flex-nowrap gap-1">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`btn btn-sm flex-shrink-0 ${videoModalTab === t.key ? "btn-primary" : "btn-outline-secondary"}`}
                  onClick={() => {
                    setVideoModalTab(t.key);
                    const vid = videoModalVideos[Number(t.key)];
                    if (vid?.otp && vid?.playback_info) {
                      setVideoModalPlayback({ otp: vid.otp, playback_info: vid.playback_info });
                    } else if (vid?.video_id) {
                      fetchVideoForTab(vid.video_id);
                    } else {
                      setVideoModalPlayback(null);
                      setVideoModalError("Video not available");
                    }
                  }}
                >
                  {t.label.charAt(0).toUpperCase() + t.label.slice(1)}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="flex-grow-1 min-h-0 overflow-auto px-3 py-3">
          {videoModalLoading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" size="sm" />
            </div>
          ) : videoModalError ? (
            <p className="text-danger mb-0">{videoModalError}</p>
          ) : videoUrl && currentVideo ? (
            <div>
              <iframe
                src={videoUrl}
                width="100%"
                height="400"
                title="Video"
                allowFullScreen
                allow="encrypted-media; autoplay; clipboard-write"
                className="border-0"
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
              />
            </div>
          ) : null}
        </div>
      </>
    );
  };

  return (
    <div className="mx-2 my-2 shadow-sm bg-white d-flex flex-column min-h-0 min-w-0" style={{ flex: "1 1 auto", minHeight: 0 }}>
      <div className="min-h-0 min-w-0" style={{ flex: "1 1 auto", minHeight: 0, overflow: "auto", scrollbarGutter: "stable" }}>
        <Table striped bordered hover size="sm" className="mb-0 align-middle w-100">
          <thead>
            <tr className="small text-body-secondary">
              {selectionMode !== "none" && (
                <th className="text-center text-nowrap fw-semibold table-light sticky-top" style={{ width: 40, zIndex: 3 }}>
                  {selectionMode === "checkbox" && showSelectAll ? (
                    <Form.Check
                      type="checkbox"
                      checked={!loading && isAllSelected}
                      disabled={loading || students.length === 0}
                      onChange={onToggleSelectAll}
                    />
                  ) : (
                    selectColumnLabel
                  )}
                </th>
              )}
              <th className="fw-semibold table-light sticky-top cursor-pointer" style={{ zIndex: 3 }} onClick={() => handleSort("student_name")}>
                <div className="d-flex align-items-center justify-content-between">
                  <span>Name</span>
                  <span><SortIcon field="student_name" /></span>
                </div>
              </th>
              <th className="text-center fw-semibold text-nowrap table-light sticky-top" style={{ zIndex: 3 }}>
                Resume
              </th>
              <th className="text-center fw-semibold text-nowrap table-light sticky-top cursor-pointer" style={{ zIndex: 3 }} onClick={() => handleSort("college")}>
                <div className="d-flex align-items-center justify-content-between">
                  <span>College</span>
                  <span><SortIcon field="college" /></span>
                </div>
              </th>
              <th className="text-center fw-semibold text-nowrap table-light sticky-top cursor-pointer" style={{ zIndex: 3 }} onClick={() => handleSort("branch")}>
                <div className="d-flex align-items-center justify-content-between">
                  <span>Branch</span>
                  <span><SortIcon field="branch" /></span>
                </div>
              </th>
              <th className="text-center fw-semibold text-nowrap table-light sticky-top cursor-pointer" style={{ zIndex: 3 }} onClick={() => handleSort("cgpa")}>
                <div className="d-flex align-items-center justify-content-between">
                  <span>CGPA</span>
                  <span><SortIcon field="cgpa" /></span>
                </div>
              </th>
              <th className="text-center fw-semibold text-nowrap table-light sticky-top cursor-pointer" style={{ zIndex: 3 }} onClick={() => handleSort("lsa")}>
                <div className="d-flex align-items-center justify-content-between">
                  <span>LSA%</span>
                  <span><SortIcon field="lsa" /></span>
                </div>
              </th>
              <th className="text-center fw-semibold text-nowrap table-light sticky-top cursor-pointer" style={{ zIndex: 3 }} onClick={() => handleSort("score")}>
                <div className="d-flex align-items-center justify-content-between">
                  <span>Score %</span>
                  <span><SortIcon field="score" /></span>
                </div>
              </th>
              <th className="text-center fw-semibold text-nowrap table-light sticky-top cursor-pointer" style={{ zIndex: 3 }} onClick={() => handleSort("category")}>
                <div className="d-flex align-items-center justify-content-between">
                  <span>Category</span>
                  <span><SortIcon field="category" /></span>
                </div>
              </th>
              <th className="text-center fw-semibold text-nowrap table-light sticky-top cursor-pointer" style={{ zIndex: 3 }} onClick={() => handleSort("grade")}>
                <div className="d-flex align-items-center justify-content-between">
                  <span>Grade</span>
                  <span><SortIcon field="grade" /></span>
                </div>
              </th>
              <th className="text-center fw-semibold text-nowrap table-light sticky-top" style={{ zIndex: 3 }}>Projects</th>
              <th className="text-center fw-semibold text-nowrap table-light sticky-top" style={{ zIndex: 3 }}>Certificates</th>
              <th className="text-center fw-semibold table-light sticky-top" style={{ zIndex: 3 }}>Primary skills</th>
              <th className="text-center fw-semibold table-light sticky-top" style={{ zIndex: 3 }}>Secondary skills</th>
              <th className="text-center fw-semibold text-nowrap table-light sticky-top" style={{ zIndex: 3 }}>Video</th>
              {showProfileColumns && (
                <th className="text-center fw-semibold text-nowrap table-light sticky-top" colSpan={4} style={{ zIndex: 3 }}>Profiles</th>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={selectionMode !== "none" ? 19 : 18} className="text-center py-4">
                  <Spinner animation="border" size="sm" />
                </td>
              </tr>
            ) : sortedStudents.length === 0 ? (
              <tr>
                <td colSpan={selectionMode !== "none" ? 19 : 18} className="text-center text-muted py-4">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              sortedStudents.map((student) => (
                <tr
                  key={student.student_id}
                  className={rowClickable ? "cursor-pointer" : undefined}
                  onClick={rowClickable ? () => onRowClick?.(student) : undefined}
                >
                  {selectionMode !== "none" && (
                    <td className="text-center" onClick={(e) => e.stopPropagation()}>
                      {selectionMode === "checkbox" ? (
                        <Form.Check
                          type="checkbox"
                          checked={isSelected?.(student.student_id) ?? false}
                          disabled={!!student.shortlisted}
                          onChange={() => onToggle?.(student)}
                        />
                      ) : (
                        <IoIosRemoveCircleOutline
                          size={18}
                          className={`${student.shortlisted ? "text-secondary cursor-not-allowed" : "text-danger cursor-pointer"}`}
                          onClick={() => !student.shortlisted && onRemove?.(student)}
                        />
                      )}
                    </td>
                  )}
                  <td className="text-nowrap">
                    <div className="d-flex align-items-center gap-2">
                      {student.profile_pic_url ? (
                        <Box
                          component="img"
                          src={resolveMediaUrl(student.profile_pic_url)}
                          alt=""
                          width={32}
                          height={32}
                          onClick={(e) => { e.stopPropagation(); setProfilePicModal({ url: student.profile_pic_url!, name: student.student_name }); }}
                          sx={{ borderRadius: "50%", objectFit: "cover", flexShrink: 0, cursor: "pointer" }}
                        />
                      ) : (
                        <Box className="rounded-circle bg-secondary bg-opacity-25 flex-shrink-0" sx={{ width: 32, height: 32 }} />
                      )}
                      <span>{student.student_name}</span>
                    </div>
                  </td>
                  <td className="text-center" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-primary"
                      title="Preview resume"
                      aria-label={`Preview resume for ${student.student_name}`}
                      onClick={() => setResumePreviewStudent(student)}
                    >
                      <FaFileAlt size={16} />
                    </button>
                  </td>
                  <td className="text-center">{student.college || "-"}</td>
                  <td className="text-center">{student.branch || "-"}</td>
                  <td className="text-center">{student.cgpa ?? "-"}</td>
                  <td className="text-center">{student.lsa != null && student.lsa !== "" ? `${student.lsa}%` : "-"}</td>
                  <td className="text-center">{student.score ?? student.score_percentage ?? "-"}</td>
                  <td className="text-center" title={student.category || undefined}>{formatCategoryColumnAbbrev(student.category)}</td>
                  <td className="text-center">{student.grade ?? "-"}</td>
                  <td className="text-center">
                    {projectsCount(student) > 0 ? (
                      <button type="button" className="btn btn-link btn-sm p-0 text-primary" onClick={(e) => { e.stopPropagation(); setProjectsModalStudent(student); setProjectsModalTab("0"); }}>
                        {projectsCount(student)}
                      </button>
                    ) : "-"}
                  </td>
                  <td className="text-center">
                    {certificatesCount(student) > 0 ? (
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCertificatesModalStudent(student);
                          const prim = student.student_primary_certificate_details?.length ?? 0;
                          const sec = student.student_secondary_certificate_details?.length ?? 0;
                          if (prim > 0) {
                            setCertificatesModalMainTab("primary");
                            setCertificatesModalTab("primary-0");
                          } else if (sec > 0) {
                            setCertificatesModalMainTab("secondary");
                            setCertificatesModalTab("secondary-0");
                          } else {
                            setCertificatesModalMainTab("primary");
                            setCertificatesModalTab("primary-0");
                          }
                        }}
                      >
                        {certificatesCount(student)}
                      </button>
                    ) : "-"}
                  </td>
                  <td className="text-center text-start text-wrap lh-sm">
                    {(student.primary_skills ?? []).length > 0 ? (student.primary_skills ?? []).join(", ") : "-"}
                  </td>
                  <td className="text-center">
                    {skillsCount(student) > 0 ? (
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-primary text-start text-wrap lh-sm"
                        onClick={(e) => { e.stopPropagation(); setSkillsModalStudent(student); setSkillsModalTab("0"); }}
                      >
                        {(student.secondary_skills ?? []).map((sk) => sk.skill_name || "-").join(", ")}
                      </button>
                    ) : "-"}
                  </td>
                  <td className="text-center">
                    {videosCount(student) > 0 ? (
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-primary"
                        onClick={(e) => openVideoStudent(e, student)}
                        title="View video"
                      >
                        <FaVideo size={18} />
                      </button>
                    ) : "-"}
                  </td>
                  {showProfileColumns && (
                    <>
                      <td className="text-center">
                        {student.linkedin ? <a href={student.linkedin} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}><FaLinkedin size={18} color="#0A66C2" /></a> : "-"}
                      </td>
                      <td className="text-center">
                        {student.leetcode ? <a href={student.leetcode} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}><SiLeetcode size={18} color="#FFA116" /></a> : "-"}
                      </td>
                      <td className="text-center">
                        {student.github ? <a href={student.github} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}><FaGithub size={18} color="#171515" /></a> : "-"}
                      </td>
                      <td className="text-center">
                        {student.hackerrank ? <a href={student.hackerrank} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}><FaHackerrank size={18} color="#2EC866" /></a> : "-"}
                      </td>
                    </>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <Modal show={!!profilePicModal} onHide={() => setProfilePicModal(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{profilePicModal?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-3 overflow-auto min-h-0" style={modalBodyScrollStyle}>
          {profilePicModal && (
            <Box component="img" src={resolveMediaUrl(profilePicModal.url)} alt={profilePicModal.name} sx={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }} />
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={!!resumePreviewStudent}
        onHide={() => setResumePreviewStudent(null)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Resume — {resumePreviewStudent?.student_name}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0 overflow-auto" style={{ ...modalBodyScrollStyle, backgroundColor: "#f5f6f8" }}>
          {resumePreviewStudent && (
            <ThemeProvider theme={hirerResumeTheme}>
              <StudentResumeView data={mapStudentToResumeData(resumePreviewStudent as ResumeStudentSource)} />
            </ThemeProvider>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={!!projectsModalStudent} onHide={() => setProjectsModalStudent(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Projects — {projectsModalStudent?.student_name}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column p-0 min-h-0" style={modalBodyScrollStyle}>
          {renderProjectsModalContent()}
        </Modal.Body>
      </Modal>

      <Modal show={!!skillsModalStudent} onHide={() => setSkillsModalStudent(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Secondary Skills — {skillsModalStudent?.student_name}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column p-0 min-h-0" style={modalBodyScrollStyle}>
          {renderSkillsModalContent()}
        </Modal.Body>
      </Modal>

      <Modal show={!!certificatesModalStudent} onHide={() => setCertificatesModalStudent(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Certificates — {certificatesModalStudent?.student_name}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column p-0 min-h-0" style={modalBodyScrollStyle}>
          {renderCertificatesModalContent()}
        </Modal.Body>
      </Modal>

      <Modal
        show={!!videoModalStudent}
        onHide={() => {
          setVideoModalStudent(null);
          setVideoModalVideos([]);
          setVideoModalPlayback(null);
        }}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {videoModalStudent && videoModalVideos.length > 0
              ? `${(videoModalVideos[Number(videoModalTab)]?.type || "Video").charAt(0).toUpperCase() + (videoModalVideos[Number(videoModalTab)]?.type || "Video").slice(1).toLowerCase()} video - ${videoModalStudent.student_name}`
              : `Videos — ${videoModalStudent?.student_name ?? ""}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column p-0 min-h-0" style={modalBodyScrollStyle}>
          {renderVideoModalContent()}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default PlacementStudentsTable;
